import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import { Button, Card, CardActions, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, IconButton, LinearProgress, ListItem, ListItemText, Tab, Tabs, Tooltip, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useHistory, useParams } from 'react-router';
import xmlFormat from 'xml-formatter';

import { DataAPIContext, GET_ONE, UPDATE } from '../api';
import { CREATE } from '../api/dataProvider';
import { isBackgroundTaskResponse, notifyBackgroundTask } from '../bg_tasks/batchResponseHelper';
import { BackgroundTaskContext } from '../bg_tasks/BackgroundTaskProvider';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import TabPanel, { a11yProps } from '../components/TabPanel';
import { ContextStore } from '../store/contextStore';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { copyTextToClipboard, toTitleCase } from '../utils';
import { useIsWidthUp } from '../utils';
import ImportListToolbar from './ImportListToolbar';
import ImportPreview from './ImportPreview';
import { getOriginDisplay, getImportStateDisplay, ORIGINS } from './utils';

const useStyles = makeStyles((theme: any) => ({
    root: {
        paddingTop: 0,
        margin: theme.spacing(2),
        marginTop: 0,
        display: 'flex',
        overflowY: 'hidden',
        overflowX: 'hidden',
        [theme.breakpoints.down('sm')]: {
            height: '100%',
        },
    },
    recordList: {
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
    listItem: {},
    recordListWrapper: {
        height: 'calc(100svh - 14rem)',
        width: '100%',

        [theme.breakpoints.down('sm')]: {
            height: 'calc(100svh - 11rem)',
            width: '100%',
        },
    },
}));

const ImportList = (props: any) => {
    const dataAPI = React.useContext(DataAPIContext);
    const isSmallScreen = !useIsWidthUp('sm');
    const theme = useTheme();
    const classes = useStyles();
    const resource = 'import_run';
    const routeParams = useParams();
    const activeImportId = routeParams.id;
    const [activeImport, setActiveImport] = React.useState(null);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const history = useHistory();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const bgCtx = React.useContext(BackgroundTaskContext);
    const { isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const [statusData, setStatusData] = React.useState(null);
    const [query, setQuery] = React.useState(null);
    const [progressTimeline, setProgressTimeline] = React.useState(null);
    const [timelineData, setTimelineData] = React.useState(null);
    const [viewXML, setViewXML] = React.useState(null);
    const [XMLData, setXMLData] = React.useState(null);
    const [statusRefreshPeriod] = React.useState(1.5); // seconds
    const [importSelection, setImportSelection] = React.useState([]);
    const [importListApiFilters, setImportListApiFilters] = React.useState([
        { field: 'status', value: [] },
        { field: 'origin', value: Object.keys(ORIGINS) },
        { field: 'from_date', value: null },
        { field: 'to_date', value: null },
    ]);
    const paramsToTab = {
        status: 0,
        list: 1,
    };
    const currentTab = paramsToTab[routeParams.tab] ? paramsToTab[routeParams.tab] : 0;
    const copyToClipboard = (text, label) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 500 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 500 });
        }
    };

    const onSelectionModelChange = (ev, selmodel) => {
        console.log('selmodel', selmodel);
        setImportSelection(selmodel);
    };

    React.useEffect(() => {
        if (currentTab !== 1) {
            return;
        }
        if (!activeImportId || activeImportId === activeImport?.id) {
            return;
        }
        dataAPI(GET_ONE, resource, { id: activeImportId }).then((res) => {
            if (res.statusCode === 200) {
                console.log('got import details', res.data);
                setActiveImport(res.data);
            } else {
                console.log('got error', res);
            }
        });
    }, [dataAPI, currentTab, activeImportId]);

    React.useEffect(() => {
        if (currentTab !== 0) {
            return;
        }
        const refreshStatus = () => {
            dataAPI(GET_ONE, resource, { urlSuffix: 'list_current_imports/' }).then((res) => {
                if (res.statusCode === 200) {
                    console.log('got status', res.data);
                    setStatusData(res.data);
                } else {
                    console.log('got error', res);
                }
            });
        };
        refreshStatus();
        const tm = setInterval(refreshStatus, statusRefreshPeriod * 1000);
        return () => {
            clearInterval(tm);
        };
    }, [dataAPI, currentTab]);

    React.useEffect(() => {
        if (currentTab !== 0) {
            return;
        }
        if (!progressTimeline) {
            return;
        }
        let active = true;
        const refreshTimeline = () => {
            dataAPI(GET_ONE, resource, { id: progressTimeline, urlSuffix: 'current_import_timeline/' }).then((res) => {
                if (!active) {
                    return;
                }
                if (res.statusCode === 200) {
                    console.log('got timeline', res.data);
                    setTimelineData(res.data);
                } else {
                    console.log('got error', res);
                }
            });
        };
        refreshTimeline();
        const tm = setInterval(refreshTimeline, statusRefreshPeriod * 1000);
        return () => {
            active = false;
            clearInterval(tm);
        };
    }, [dataAPI, progressTimeline]);

    React.useEffect(() => {
        if (currentTab !== 0) {
            return;
        }
        if (!viewXML) {
            return;
        }
        let active = true;
        dataAPI(GET_ONE, 'import_run', { id: viewXML, urlSuffix: 'input_data/' }).then((res) => {
            if (!active) {
                return;
            }
            if (res.statusCode === 200) {
                setXMLData(res.data.input_data);
            } else {
                enqueueSnackbar('Error fetching input data', { variant: 'error' });
            }
        });
        return () => {
            active = false;
        };
    }, [dataAPI, viewXML, XMLData]);

    const [relatedLoading, setRelatedLoading] = React.useState(false);

    const listItemRenderer = (row: any, idx: number, rows: any, gridApi: any) => {
        return (
            <>
                <ListItem alignItems="flex-start" className={classes.listItem} onClick={() => onRowClick(row)}>
                    <ListItemText primary={row.id} secondary={moment(row.created_ts).format('MMMM Do YYYY, h:mm:ss a')} />
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </>
        );
    };

    const handleTabChange = (ev, newTab) => {
        const match = Object.keys(paramsToTab).filter((x) => paramsToTab[x] === newTab);
        console.log('newTab', newTab, match);
        if (match && match.length === 1) {
            history.push(`/import/run/${match[0]}`);
        }
    };

    const tabPanelProps = (index, h) => {
        return {
            className: classes.tabPanel,
            value: currentTab,
            index: index,
            dir: theme.direction,
        };
    };

    const handleCopyToClipboard = (ev, value, label) => {
        ev.stopPropagation();
        copyToClipboard(value, label)
    }
    const columns = [
        ...getLicenseeColumn(isFranchise, licensees, currentLicensee, { field: 'top' }),
        { field: 'created_ts', headerName: 'Import started at', sortable: true, flex: 1, filterable: false, valueGetter: (p: any) => moment.utc(p.row.created_ts).format('MMMM Do YYYY, h:mm:ss a z') },
        { field: 'processing_started_ts', headerName: 'Processing started at', sortable: true, filterable: false, flex: 1, valueGetter: (p: any) => moment.utc(p.row.processing_started_ts).format('MMMM Do YYYY, h:mm:ss a z') },
        { field: 'processing_finished_ts', headerName: 'Processing finished at', sortable: true, filterable: false, flex: 1, valueGetter: (p: any) => moment.utc(p.row.processing_started_ts).format('MMMM Do YYYY, h:mm:ss a z') },
        { field: 'processing_duration', headerName: 'Processing time', sortable: true, filterable: false, flex: 0.4, valueGetter: (p: any) => `${p.row.processing_duration?.toFixed(2) ?? 0} s` ?? '-' },
        {
            field: 'group',
            headerName: 'Group ID',
            sortable: false,
            filterable: false,
            flex: 1,
            renderCell: (p: any) => (
                <>
                    {p.row.group ? <>{p.row.group}{' '}
                    <Tooltip title="Copy group ID">
                        <IconButton size="small" onClick={(ev) =>   handleCopyToClipboard(ev, p.row.group, 'Import group ID')  }>
                            <FileCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    </> : <></> }
                </>
            ),
        },
        {
            field: 'id',
            headerName: 'Import ID',
            sortable: false,
            filterable: false,
            flex: 1,
            renderCell: (p: any) => (
                <>
                    {p.row.id}{' '}
                    <Tooltip title="Copy import ID">
                        <IconButton size="small" onClick={(ev) =>   handleCopyToClipboard(ev, p.row.id, 'Import ID')  }>
                            <FileCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </>
            ),
        },
        { field: 'status', headerName: 'Status', sortable: false, filterable: false, flex: 1, valueGetter: (p: any) => toTitleCase(getImportStateDisplay(p.row.state ?? 0)) },
        { field: 'origin', headerName: 'Origin', sortable: false, filterable: false, flex: 0.6, valueGetter: (p: any) => getOriginDisplay(p.row.origin ?? 0), visible: false },
        { field: 'source_id', headerName: 'Source ID', sortable: false, filterable: false, flex: 0.6, valueGetter: (p: any) => p.row.sources?.map((x: any) => x.source.code).join(', ') },
        { field: 'source_name', headerName: 'Source name', sortable: false, filterable: false, flex: 1, valueGetter: (p: any) => p.row.sources?.map((x: any) => x.source.name).join(', ') },
    ];
    const gridInitialState = {
        columns: {
            columnVisibilityModel: {
                origin: true,
                source_id: false,
                source_name: false,
                processing_started_ts: false,
                processing_finished_ts: false,
            },
        },
    };

    // const cols: any = isSmallScreen ? (activeImport?.id ? 0 : 12) : activeImport?.id ? 8 : 12;
    const cols: number = 12;

    const onRowClick = (row: any) => {
        setActiveImport(isSmallScreen ? row : row.row);
        history.push(`/import/run/list/${row.id}${window.location.search}`);
    };

    const onRunClick = (run_id, ev) => {
        console.log('going to', run_id);
        // setActiveImport(run);
        setActiveImport(null);
        history.push(`/import/run/list/${run_id}${window.location.search}`);
    }

    const onShowProgressTimeline = (ev, run) => {
        setProgressTimeline(run.run_id);
    };

    const onProgressTimelineClose = (ev) => {
        setProgressTimeline(null);
        setTimelineData(null);
    };

    const onViewXML = (ev, run) => {
        setViewXML(run.run_id);
    };

    const onViewXMLClose = (ev) => {
        setViewXML(null);
        setXMLData(null);
    };

    const onCopyXML = (ev) => {
        copyToClipboard(XMLData, 'XML');
    };

    const onStopImport = (ev, run) => {
        if (window.confirm(`Are you sure you want to stop ${run.run?.task_id ? 'import' : 'monitoring import'} ${run.run_id}?`)) {
            dataAPI(UPDATE, resource, { id: run.run_id, urlSuffix: 'stop_import/' }).then((res) => {
                if (res.statusCode === 200) {
                    enqueueSnackbar(`Import ${run.run_id} ${run.run?.task_id ? 'stopped' : 'removed from monitoring' } successfully`, { variant: 'success' });
                } else {
                    enqueueSnackbar(`Error stopping import ${run.run_id}`, { variant: 'error' });
                }
            });
        }
    };

    const onReimportSelection = (ev) => {
        console.log('reimport: selmodel', importSelection);
        if (!window.confirm(`Are you sure you want to reimport ${importSelection.length} records?`)) {
            return enqueueSnackbar('Reimport cancelled', { variant: 'default' });
        }
        const sendNotifications = window.confirm(`Do you want to send notifications for these imports?`);
        dataAPI(CREATE, resource, { data: { action: 'resubmit', data: { send_notifications: sendNotifications, list: importSelection } }, urlSuffix: 'batch/' }).then((res) => {
            if (isBackgroundTaskResponse(res)) {
                notifyBackgroundTask(res, enqueueSnackbar, closeSnackbar, bgCtx);
                setTimeout(() => {
                    setLastUpdate(new Date().toString());
                }, 1000);
            } else if (res.statusCode === 201) {
                enqueueSnackbar(`${importSelection.length} import${importSelection.length > 1 ? 's' : ''} scheduled for reimport`, { variant: 'success' });
                setTimeout(() => {
                    setLastUpdate(new Date().toString());
                }, 1000);
            } else {
                enqueueSnackbar(`Error scheduling ${importSelection.length} import${importSelection.length > 1 ? 's' : ''} for reimport`, { variant: 'error' });
            }
        });
    };

    const onQueryChange = (query) => {
        setQuery(query);
    };

    const onApiFilterChange = (filter) => {
        setImportListApiFilters((prevState) => {
            console.log('api filters change', prevState, filter);
            return prevState.map((x) => (x.field !== filter.field ? x : filter));
        });
    };

    const onImportPreviewClose = (ev) => {
        console.log('onImportPreviewClose', activeImport);
        setActiveImport(null);
        const new_url = `${window.location.pathname.replace(new RegExp(`/${activeImport.id}`), "")}${window.location.search}`;
        history.push(new_url);
    };

    const onImportReimported = (run) => {
        setActiveImport(run);
    };

    return (
        <div className={classes.root}>
            <PageMeta title="Import" />
            {activeImport && <ImportPreview
                id={activeImport?.id} 
                data={activeImport} 
                onClose={onImportPreviewClose} 
                onImportReimported={onImportReimported} 
                onRunClick={onRunClick}
            />}
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12} sx={{ pb: '0 !important' }}>
                    <PageHeading sx={{ mt: 2, mb: 1 }}>Import</PageHeading>
                    <Tabs className={classes.tabsMenu} value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" variant="scrollable">
                        <Tab label="Running imports" {...a11yProps(0)} />
                        <Tab label="All imports" {...a11yProps(1)} />
                    </Tabs>
                </Grid>
                <Grid item xs={12} sx={{ pt: '4px !important' }}>
                    <TabPanel {...tabPanelProps(0)}>
                        {progressTimeline && (
                            <>
                                <Dialog fullWidth={true} maxWidth="xl" open={!!progressTimeline} onClose={onProgressTimelineClose}>
                                    <DialogTitle>Import {progressTimeline}: progress timeline</DialogTitle>
                                    <DialogContent>
                                        {!timelineData && <LinearProgress />}
                                        {timelineData && (
                                            <>
                                                <Box sx={{ height: '400px' }}>
                                                    <DataGrid
                                                        getRowId={(row) => row.ts}
                                                        rows={timelineData.stage_log}
                                                        columns={[
                                                            { field: 'ts', headerName: 'Timestamp', flex: 1, valueFormatter: (p) => moment.utc(p.value).format() },
                                                            { field: 'message', headerName: 'Message', flex: 5 },
                                                        ]}
                                                    />
                                                </Box>
                                            </>
                                        )}
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={onProgressTimelineClose}>Close</Button>
                                    </DialogActions>
                                </Dialog>
                            </>
                        )}
                        {viewXML && (
                            <>
                                <Dialog fullWidth={true} maxWidth="xl" open={!!viewXML} onClose={onViewXMLClose}>
                                    <DialogTitle>Import {viewXML}: XML data</DialogTitle>
                                    <DialogContent>
                                        {!XMLData && <LinearProgress />}
                                        {XMLData && (
                                            <>
                                                <Box sx={{ height: '400px' }}>
                                                    <Typography>
                                                        <pre>
                                                            {xmlFormat(XMLData, {
                                                                collapseContent: true,
                                                            })}
                                                        </pre>
                                                    </Typography>
                                                </Box>
                                            </>
                                        )}
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={onCopyXML}>Copy to clipboard</Button>
                                        <Button onClick={onViewXMLClose}>Close</Button>
                                    </DialogActions>
                                </Dialog>
                            </>
                        )}
                        {(!statusData || !statusData.length) && (
                            <>
                                <Typography variant="body1">No imports are currently running</Typography>
                            </>
                        )}
                        {statusData && statusData.length > 0 && (
                            <>
                                <Typography variant="body2">
                                    <Grid container direction="row" spacing={2}>
                                        {statusData.map((x) => {
                                            const cts = moment.utc(x.created_ts);
                                            const uts = moment.utc(x.updated_ts);
                                            const diff = moment.utc().diff(cts, 'seconds');
                                            const dur = moment.duration(diff, 'seconds');
                                            return (
                                                <Grid item xs={12} key={x.run_id}>
                                                    <Card sx={{ minWidth: 380, maxWidth: 800 }} variant="outlined">
                                                        <CardContent>
                                                            <Grid container direction="row" spacing={2}>
                                                                <Grid item xs={8}>
                                                                    Import {x.run_id}{' '}
                                                                    {x.stuck_probability >= 90 && (
                                                                        <Typography display="inline" color="secondary">
                                                                            is likely stuck
                                                                        </Typography>
                                                                    )}
                                                                </Grid>
                                                                <Grid item xs={4}>
                                                                    Origin: {getOriginDisplay(x.run?.origin)}
                                                                </Grid>
                                                                <Grid item xs={6} md={4}>
                                                                    Started at
                                                                    <br />
                                                                    {cts.format()}
                                                                </Grid>
                                                                <Grid item xs={6} md={4}>
                                                                    Last updated at
                                                                    <br />
                                                                    {uts.format()}
                                                                </Grid>
                                                                <Grid item xs={12} md={4}>
                                                                    Elapsed: {dur.humanize()}
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    Last stage: {x.stage}
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <LinearProgress variant="determinate" value={x.percent} />
                                                                </Grid>
                                                            </Grid>
                                                        </CardContent>
                                                        <CardActions sx={{ justifyContent: 'space-between' }}>
                                                            <div>
                                                                <Button onClick={(ev) => onShowProgressTimeline(ev, x)}>View progress timeline</Button>
                                                                <Button onClick={(ev) => onViewXML(ev, x)}>View XML</Button>
                                                            </div>
                                                            <div />
                                                            <Button color="secondary" onClick={(ev) => onStopImport(ev, x)}>
                                                                { x.run?.task_id ? "Stop import" : "Stop monitoring" }
                                                            </Button>
                                                        </CardActions>
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Typography>
                            </>
                        )}
                    </TabPanel>
                </Grid>
                <Grid item xs={12}>
                    <TabPanel {...tabPanelProps(1)}>
                        <Grid container direction="row">
                            <Grid item xs={12}>
                                <ImportListToolbar
                                    resource={resource}
                                    isSmallScreen={isSmallScreen}
                                    setLastUpdate={setLastUpdate}
                                    setRelatedLoading={setRelatedLoading}
                                    onQueryChange={onQueryChange}
                                    onApiFilterChange={onApiFilterChange}
                                    onReimportSelection={onReimportSelection}
                                    selection={importSelection}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container direction="row" spacing={2}>
                                    {isSmallScreen && activeImport?.id ? null : (
                                        <Grid item xs={cols} className={classes.recordListWrapper}>
                                            <RecordList
                                                className={classes.recordList}
                                                resource={resource}
                                                disableSelectionOnClick={true}
                                                onRowClick={onRowClick}
                                                noItemsFoundMessage={'No imports found.'}
                                                sortModel={[{ field: 'created_ts', sort: 'desc' }]}
                                                relatedLoading={relatedLoading}
                                                checkboxSelection={true}
                                                selectionModel={importSelection}
                                                onSelectionModelChange={onSelectionModelChange}
                                                query={query}
                                                apiFilters={importListApiFilters}
                                                deps={lastUpdate}
                                                perPage={25}
                                                listItemRenderer={listItemRenderer}
                                                initialState={gridInitialState}
                                                columns={columns}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </TabPanel>
                </Grid>
            </Grid>
        </div>
    );
};

export default ImportList;
